import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const TeamMembers = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://atabackend.quindl.com/teammanagement/get');
        const data = await response.json();
        setTeamMembers(data);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to fetch team members');
        setIsLoading(false);
        console.error('Error fetching team members:', err);
      }
    };

    fetchTeamMembers();
  }, []);

  const handleImageError = (e) => {
    e.target.style.display = 'none';
  };

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center p-4">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <span className="text-gray-600">Loading team members...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center p-4">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Our Team</h2>
          <p className="mt-4 text-lg text-gray-500">
            Meet the talented individuals behind our success
          </p>
        </div>

        {teamMembers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No team members found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <div
                key={member._id}
                className="bg-white rounded-xl shadow-md overflow-hidden transform transition duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer"
                onClick={() => handleMemberClick(member)}
              >
                <div className="aspect-w-1 aspect-h-1 bg-gray-200">
                  {member.image ? (
                    <img
                      src={`https://atabackend.quindl.com/uploads/${member.image}`}
                      alt={member.name}
                      onError={handleImageError}
                      className="w-full h-64 object-fit"
                    />
                  ) : (
                    <div className="w-full h-64 flex items-center justify-center bg-gray-100">
                      <svg
                        className="h-20 w-20 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-sm font-medium text-blue-600 mb-3">
                    {member.designation}
                  </p>
                  <p className="text-gray-500 text-sm line-clamp-3 mb-4">
                    {member.description}
                  </p>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMemberClick(member);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                  >
                    Read More
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-4 w-4 ml-1" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 5l7 7-7 7" 
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal/Popup */}
        {isModalOpen && selectedMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="relative">
                <button
                  onClick={handleCloseModal}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
                <div className="grid md:grid-cols-2 gap-8 p-8">
                  <div className="space-y-4">
                    {selectedMember.image ? (
                      <img
                        src={`https://atabackend.quindl.com/uploads/${selectedMember.image}`}
                        alt={selectedMember.name}
                        className="w-full h-auto rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-lg">
                        <svg
                          className="h-20 w-20 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {selectedMember.name}
                    </h2>
                    <p className="text-lg font-medium text-blue-600">
                      {selectedMember.designation}
                    </p>
                    <div className="prose prose-sm text-gray-500">
                      <p className="whitespace-pre-wrap">{selectedMember.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMembers;